'use client';
import { useState, useEffect } from 'react';
import {
  LuMapPin,
  LuPhone,
  LuMail,
  LuClock,
  LuSend,
  LuLoader,
  LuArrowRight,
  LuCheck,
  LuX,
} from 'react-icons/lu';
import { useUser } from '@clerk/nextjs';

interface ContactSectionProps {
  pageContext?: string;
}

const ContactSection = ({ pageContext }: ContactSectionProps) => {
  const { user, isSignedIn } = useUser();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  useEffect(() => {
    if (isSignedIn && user) {
      setFormData((prev) => ({
        ...prev,
        name: user.fullName || '',
        email: user.primaryEmailAddress?.emailAddress || '',
      }));
    }
  }, [isSignedIn, user]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(
    null
  );

  const services = [
    'Land Survey',
    'GIS Services',
    'Property Management',
    'Cyber Services',
    'Qwetu Gas Supplier',
  ];

  const contactInfo = [
    {
      icon: <LuMapPin className='h-6 w-6' />,
      title: 'Our Location',
      details: 'Malindi, Opposite Mbuzi Wengi',
      gradient: 'from-blue-400 to-blue-600',
      highlightColor: 'bg-blue-100',
      iconBg: 'bg-blue-500',
    },
    {
      icon: <LuPhone className='h-6 w-6' />,
      title: 'Phone Number',
      details: '+254 756 211 546',
      gradient: 'from-emerald-400 to-emerald-600',
      highlightColor: 'bg-emerald-100',
      iconBg: 'bg-emerald-500',
    },
    {
      icon: <LuMail className='h-6 w-6' />,
      title: 'Email Address',
      details: 'info@qwetulands.com',
      gradient: 'from-amber-400 to-amber-600',
      highlightColor: 'bg-amber-100',
      iconBg: 'bg-amber-500',
    },
    {
      icon: <LuClock className='h-6 w-6' />,
      title: 'Working Hours',
      details: 'Mon - Sat: 8:00 AM - 6:00 PM',
      gradient: 'from-purple-400 to-purple-600',
      highlightColor: 'bg-purple-100',
      iconBg: 'bg-purple-500',
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Submit to our integrated contact API that handles Clerk user creation and booking
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // Check if we got a proper JSON response
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Response is not JSON:', text.substring(0, 200));
        throw new Error('Unexpected response from server');
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to submit contact form');
      }

      // Success! Clear form and show success message
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
      });

      console.log('Contact form submitted successfully', {
        isNewUser: data.isNewUser,
        bookingId: data.booking?.id,
      });
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <section className='relative py-24 overflow-hidden bg-linear-to-b from-white to-blue-50'>
      {/* Decorative Elements */}
      <div className='absolute top-0 left-0 right-0 h-1.5 bg-linear-to-r from-[#00B0F0] via-[#FFA500] to-[#00B050]'></div>

      {/* Background Pattern */}
      <div className='absolute inset-0 overflow-hidden pointer-events-none'>
        <div className="absolute inset-0 opacity-10 bg-[url('/images/patterns/circuit-pattern.svg')] bg-repeat"></div>

        {/* Floating Orbs */}
        <div className='absolute -top-20 -right-20 h-80 w-80 rounded-full bg-[#00B0F0]/10 blur-3xl'></div>
        <div className='absolute -bottom-40 -left-20 h-96 w-96 rounded-full bg-[#FFA500]/10 blur-3xl'></div>
        <div className='absolute top-1/2 left-1/3 h-64 w-64 rounded-full bg-[#00B050]/10 blur-3xl'></div>
      </div>

      <div className='container relative mx-auto px-4 z-10'>
        {/* Section Header */}
        <div className='mx-auto mb-16 max-w-2xl text-center'>
          <span className='inline-block py-1 px-3 mb-4 rounded-full text-sm font-semibold text-[#00B0F0] bg-[#00B0F0]/10'>
            Contact Us
          </span>
          <h2 className='mb-4 text-3xl font-bold lg:text-5xl bg-linear-to-r from-[#00B0F0] via-[#0090D0] to-[#00B050] bg-clip-text text-transparent'>
            Let's Connect
          </h2>
          <p className='text-lg text-gray-600'>
            Have questions or need assistance? We're here to help you with all
            your service needs.
          </p>
        </div>

        <div className='mx-auto max-w-7xl'>
          <div className='grid gap-10 lg:grid-cols-2'>
            {/* Left Column - Contact Form */}
            <div className='order-2 lg:order-1'>
              <div className='rounded-3xl bg-white p-6 shadow-xl lg:p-8 border border-[#00B0F0]/10 backdrop-blur-xs hover:shadow-2xl hover:shadow-[#00B0F0]/10 transition-all duration-500'>
                <h3 className='mb-6 text-2xl font-semibold text-gray-900'>
                  Send Us a <span className='text-[#00B0F0]'>Message</span>
                </h3>
                <form onSubmit={handleSubmit} className='space-y-6'>
                  <div className='grid gap-6 md:grid-cols-2'>
                    <div>
                      <label className='mb-2 block text-sm font-medium text-gray-700'>
                        Your Name
                      </label>
                      <input
                        type='text'
                        name='name'
                        required
                        value={formData.name}
                        onChange={handleChange}
                        readOnly={isSignedIn}
                        className='w-full rounded-lg border border-gray-200 p-3 text-sm focus:border-[#00B0F0] focus:ring-[#00B0F0]/30 transition-all duration-300'
                        placeholder='John Doe'
                      />
                    </div>

                    <div>
                      <label className='mb-2 block text-sm font-medium text-gray-700'>
                        Your Email
                      </label>
                      <input
                        type='email'
                        name='email'
                        required
                        value={formData.email}
                        onChange={handleChange}
                        readOnly={isSignedIn}
                        className='w-full rounded-lg border border-gray-200 p-3 text-sm focus:border-[#00B0F0] focus:ring-[#00B0F0]/30 transition-all duration-300'
                        placeholder='john@example.com'
                      />
                    </div>
                  </div>

                  <div className='grid gap-6 md:grid-cols-2'>
                    <div>
                      <label className='mb-2 block text-sm font-medium text-gray-700'>
                        Phone Number
                      </label>
                      <input
                        type='tel'
                        name='phone'
                        value={formData.phone}
                        onChange={handleChange}
                        className='w-full rounded-lg border border-gray-200 p-3 text-sm focus:border-[#00B0F0] focus:ring-[#00B0F0]/30 transition-all duration-300'
                        placeholder='+254 XXX XXX XXX'
                      />
                    </div>

                    <div>
                      <label className='mb-2 block text-sm font-medium text-gray-700'>
                        Service Interested In
                      </label>
                      <select
                        name='service'
                        value={formData.service}
                        onChange={handleChange}
                        className='w-full rounded-lg border border-gray-200 p-3 text-sm focus:border-[#00B0F0] focus:ring-[#00B0F0]/30 transition-all duration-300'
                      >
                        <option value=''>Select a service</option>
                        {services.map((service) => (
                          <option key={service} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className='mb-2 block text-sm font-medium text-gray-700'>
                      Your Message
                    </label>
                    <textarea
                      name='message'
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className='w-full rounded-lg border border-gray-200 p-3 text-sm focus:border-[#00B0F0] focus:ring-[#00B0F0]/30 transition-all duration-300'
                      placeholder='How can we help you?'
                    />
                  </div>

                  {/* Status Messages */}
                  {submitStatus === 'success' && (
                    <div className='rounded-lg bg-emerald-50 p-4 text-sm text-emerald-600 flex items-center'>
                      <LuCheck className='h-5 w-5 mr-2 shrink-0' />
                      <span>
                        Thank you! Your message has been sent successfully.
                      </span>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className='rounded-lg bg-red-50 p-4 text-sm text-red-600 flex items-center'>
                      <LuX className='h-5 w-5 mr-2 shrink-0' />
                      <span>
                        Sorry, there was an error sending your message. Please
                        try again.
                      </span>
                    </div>
                  )}

                  {/* Submit Button */}
                  <div>
                    <button
                      type='submit'
                      disabled={isSubmitting}
                      className='group relative w-full rounded-lg overflow-hidden bg-linear-to-r from-[#00B0F0] to-[#00B050] px-8 py-3 text-center text-sm font-medium text-white transition-all duration-300 hover:shadow-lg hover:shadow-[#00B0F0]/25 disabled:opacity-70'
                    >
                      <span className='relative z-10 flex items-center justify-center'>
                        {isSubmitting ? (
                          <LuLoader className='mr-2 h-4 w-4 animate-spin' />
                        ) : (
                          <LuSend className='mr-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1' />
                        )}
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </span>
                      <span className='absolute inset-0 -translate-y-full bg-linear-to-r from-[#0090D0] to-[#009040] transition-transform duration-300 group-hover:translate-y-0'></span>
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Right Column - Contact Info & Map */}
            <div className='order-1 lg:order-2'>
              {/* Contact Info Cards */}
              <div className='mb-8 grid gap-4 sm:grid-cols-2'>
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    className='group relative overflow-hidden rounded-xl bg-white p-5 shadow-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-xl border border-transparent hover:border-[#00B0F0]/10'
                  >
                    {/* Top accent bar */}
                    <div
                      className={`absolute top-0 left-0 right-0 h-1 bg-linear-to-r ${info.gradient}`}
                    ></div>

                    <div className='flex items-center gap-4'>
                      <div
                        className={`relative rounded-xl ${info.iconBg} p-3 text-white transition-transform duration-300 group-hover:scale-110 shadow-lg`}
                      >
                        {/* Pulsing effect */}
                        <span
                          className={`absolute inset-0 rounded-xl ${info.iconBg} animate-ping opacity-30`}
                        ></span>
                        {info.icon}
                      </div>
                      <div>
                        <h3 className='font-semibold text-gray-900'>
                          {info.title}
                        </h3>
                        <p className='text-gray-600'>{info.details}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Map with decorative frame */}
              <div className='relative p-2 bg-linear-to-r from-[#00B0F0] via-[#FFA500] to-[#00B050] rounded-3xl shadow-xl'>
                <div className='overflow-hidden rounded-2xl bg-white'>
                  <iframe
                    src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3989.4467453559797!2d40.11061697576499!3d-3.2237064355698086!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x18158fa297c147d1%3A0x786f41aeaefee1f6!2sNjeri%20Mini%20Shop!5e0!3m2!1sen!2ske!4v1712064492123!5m2!1sen!2ske'
                    className='h-[400px] w-full'
                    style={{ border: 0 }}
                    allowFullScreen
                    loading='lazy'
                    referrerPolicy='no-referrer-when-downgrade'
                  />
                </div>

                {/* Visit us CTA at the bottom of the map */}
                <div className='absolute -bottom-5 left-1/2 transform -translate-x-1/2'>
                  <div className='flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-lg'>
                    <div className='text-[#00B0F0] font-medium'>Visit Us</div>
                    <div className='w-2 h-2 rounded-full bg-[#00B0F0] animate-pulse'></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
